import React from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HubWeight } from './types';

import { opsColors } from '../../theme/opsTokens';

interface HubWeightingStepProps {
  hubWeights: HubWeight[];
  onHubWeightChange: (hubId: string, percentage: number) => void;
}

export default function HubWeightingStep({ hubWeights, onHubWeightChange }: HubWeightingStepProps) {
  const { t } = useTranslation('onboarding');
  const hubTotal = hubWeights.reduce((sum, hub) => sum + Math.round(hub.percentage), 0);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('hubWeighting.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('hubWeighting.description')}
      </Typography>

      <Box
        sx={(theme) => ({
          border: `1px solid ${opsColors.neutral.line}`,
          borderRadius: 2,
          backgroundColor: opsColors.neutral.panel,
          p: 2,
        })}
      >
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          {hubWeights.map((hub) => (
            <Box key={hub.id} sx={{ display: 'grid', gridTemplateColumns: '1fr minmax(120px, 180px)', gap: 1.25 }}>
              <Typography sx={{ alignSelf: 'center' }}>{hub.name}</Typography>
              <TextField
                label={t('hubWeighting.demandShare')}
                type="number"
                value={hub.percentage}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 1,
                  'data-testid': `wizard.hub-weight.percentage-input.${hub.id}`,
                }}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  onHubWeightChange(hub.id, Number.isFinite(value) ? value : 0);
                }}
              />
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
          {t('hubWeighting.total', { value: hubTotal })}
        </Typography>
        {hubTotal !== 100 ? (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t('hubWeighting.warning')}
          </Alert>
        ) : null}
      </Box>
    </Box>
  );
}
